import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, TextField, Box, Typography, Autocomplete, Chip, IconButton, Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { groupService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const GroupForm = ({ open, onClose, onSubmit, group = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    members: []
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const { showToast } = useToast();
  const autocompleteRef = useRef(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courseResults, setCourseResults] = useState([]);
  const [enrolmentPreview, setEnrolmentPreview] = useState([]);

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        description: group.description || '',
        members: []
      });
      fetchGroupMembers(group.id);
    } else {
      setFormData({
        name: '',
        description: '',
        members: []
      });
      setSelectedMembers([]);
    }
  }, [group]);

  useEffect(() => {
    let cancelled = false;

    const loadEnrolmentPreview = async () => {
      const memberIds = selectedMembers.map((member) => member.id);
      const courseIds = selectedCourses.map((course) => course.id);

      if (memberIds.length === 0 || courseIds.length === 0) {
        setEnrolmentPreview([]);
        return;
      }

      try {
        const response = await groupService.previewEnrolment(
          memberIds,
          courseIds
        );

        if (!cancelled) {
          setEnrolmentPreview(response.data.preview || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading enrolment preview:', error);
          setEnrolmentPreview([]);
        }
      }
    };

    loadEnrolmentPreview();

    return () => {
      cancelled = true;
    };
  }, [selectedMembers, selectedCourses]);

  

  const fetchGroupMembers = async (groupId) => {
    try {
      setLoading(true);
      const courseResponse =  await groupService.getGroupCourses(group.id);
      setSelectedCourses(courseResponse.data.courses || []);
      const response = await groupService.getGroupMembers(groupId);
      if (response.data.success) {
        setSelectedMembers(response.data.groupMembers);
      }
    } catch (error) {
      console.error('Error fetching group members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSearchMembers = async (term) => {
    setSearchTerm(term);
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await groupService.searchMembers(term);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching members:', error);
      setSearchResults([]);
    }
  };

  const handleAddMember = (member) => {
    if (!selectedMembers.find(m => m.id === member.id)) {
      setSelectedMembers([...selectedMembers, member]);
    }
    setSearchTerm('');
    setTimeout(() => {
      if (autocompleteRef.current) {
        const input = autocompleteRef.current.querySelector('input');
        if (input) {
          input.focus();
        }
      }
    }, 50);

  };

  const handleRemoveMember = (memberId) => {
    setSelectedMembers(selectedMembers.filter(m => m.id !== memberId));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showToast('Group name is required', 'error');
      return;
    }

    try {
      setLoading(true);
      const memberIds = selectedMembers.map(member => member.id);
      
      if (group) {
        // Update existing group
        const response = await groupService.updateGroup(group.id, {
          ...formData,
          members: memberIds,
          required_course_ids: selectedCourses.map((course) => course.id)
        });
        
        if (response.data.success) {
          showToast('Group updated successfully', 'success');
          onSubmit();
        }
      } else {
        // Create new group
        const response = await groupService.createGroup({
          ...formData,
          members: memberIds,
          required_course_ids: selectedCourses.map((course) => course.id)
        });
        
        if (response.data.success) {
          showToast('Group created successfully', 'success');
          onSubmit();
        }
      }
    } catch (error) {
      console.error('Error saving group:', error);
      showToast(error.response?.data?.message || 'Failed to save group', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{group ? 'Edit Cohort' : 'Create Cohort'}</Typography>
              <IconButton onClick={onClose} size="small">
                  <CloseIcon />
              </IconButton>
          </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            required
          />
          
          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={2}
          />
          
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Add Users & Cohorts
            </Typography>
            <Autocomplete
              ref={autocompleteRef}
              freeSolo
              options={searchResults}
              getOptionLabel={(option) => option.name || ''}
              inputValue={searchTerm}
              onInputChange={(event, newValue) => handleSearchMembers(newValue)}
              onChange={(event, newValue) => {
                if (newValue && typeof newValue === 'object') {
                  handleAddMember(newValue);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label=" Search Users or Cohorts"
                  variant="outlined"
                  fullWidth
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box sx={{ display: 'flex', flexDirection: 'column'}}>
                      <Typography variant="body1"> {option.name} <Typography variant="caption" color="text.secondary">{option.type === 'user' ? 'User' : 'Group'}</Typography></Typography>
                    </Box>
                  </li>
                );
              }}
              value={null} clearOnBlur={false} disableClearable={true}  openOnFocus={true}
            />
            
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedMembers.map((member) => (
                <Chip
                  key={member.id}
                  label={member.name}
                  onDelete={() => handleRemoveMember(member.id)}
                  color={member.type === 'user' ? 'primary' : 'secondary'}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
          <Box>
            <Autocomplete
              options={courseResults}
              getOptionLabel={(course) => course.name}
              value={null}
              onInputChange={async (event, value) => {
                if (value.length < 2) {
                  setCourseResults([]);
                  return;
                }

                const response =
                  await groupService.searchRequiredCourses(value);

                setCourseResults(response.data.courses || []);
              }}
              onChange={(event, course) => {
                if (
                  course &&
                  !selectedCourses.some(
                    (selectedCourse) => selectedCourse.id === course.id
                  )
                ) {
                  setSelectedCourses([
                    ...selectedCourses,
                    course
                  ]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Required courses"
                />
              )}
            />
            <Box sx={{ mt: 2 }}>
              {selectedCourses.map((course) => (
                <Chip
                  key={course.id}
                  label={course.name}
                  onDelete={() =>
                    setSelectedCourses(
                      selectedCourses.filter(
                        (selectedCourse) =>
                          selectedCourse.id !== course.id
                      )
                    )
                  }
                />
              ))}

              {enrolmentPreview.length > 0 && (
                <Alert severity="info">
                  {enrolmentPreview.map((item) => (
                    <div key={`${item.user_id}-${item.course_id}`}>
                      {item.user_name} will be enrolled in {item.course_name} and it will be due on {item.due_date || 'the course end date'}.
                    </div>
                  ))}
                </Alert>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : (group ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupForm;
